import type { Question, QuestionValidation, Language } from '@nordcrest/shared';
import { t } from '../i18n';

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export function validateAnswer(
  value: string | string[] | number | boolean | null,
  question: Question,
  language: Language
): ValidationResult {
  // Check required
  if (question.required) {
    if (value === null || value === undefined) {
      return { valid: false, error: t('errors.required', language) };
    }

    if (typeof value === 'string' && value.trim() === '') {
      return { valid: false, error: t('errors.required', language) };
    }

    if (Array.isArray(value) && value.length === 0) {
      return { valid: false, error: t('errors.required', language) };
    }
  }

  // If not required and empty, it's valid
  if (value === null || value === undefined || value === '') {
    return { valid: true };
  }

  // Type-specific validation
  const validation = question.validation;

  switch (question.type) {
    case 'email':
      return validateEmail(value as string, language);

    case 'phone':
      return validatePhone(value as string, language);

    case 'text':
    case 'textarea':
      return validateText(value as string, validation, language);

    case 'multi_select':
      return validateMultiSelect(value as string[], validation, language);

    case 'scale':
      return validateNumber(value as number, validation, language);

    default:
      return { valid: true };
  }
}

function validateEmail(value: string, language: Language): ValidationResult {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return { valid: false, error: t('errors.email', language) };
  }
  return { valid: true };
}

function validatePhone(value: string, language: Language): ValidationResult {
  // Allow empty for optional fields
  if (!value.trim()) {
    return { valid: true };
  }

  // Swedish phone number format
  const cleaned = value.replace(/[\s-]/g, '');
  const phoneRegex = /^(\+46|0)[0-9]{6,12}$/;

  if (!phoneRegex.test(cleaned)) {
    return { valid: false, error: t('errors.phone', language) };
  }

  return { valid: true };
}

function validateText(
  value: string,
  validation: QuestionValidation | undefined,
  language: Language
): ValidationResult {
  if (!validation) return { valid: true };

  if (validation.minLength && value.length < validation.minLength) {
    return {
      valid: false,
      error: t('errors.required', language),
    };
  }

  if (validation.maxLength && value.length > validation.maxLength) {
    return {
      valid: false,
      error: `Max ${validation.maxLength} characters`,
    };
  }

  if (validation.pattern) {
    const regex = new RegExp(validation.pattern);
    if (!regex.test(value)) {
      return { valid: false, error: t('errors.required', language) };
    }
  }

  return { valid: true };
}

function validateMultiSelect(
  value: string[],
  validation: QuestionValidation | undefined,
  language: Language
): ValidationResult {
  if (!validation) return { valid: true };

  if (validation.minSelections && value.length < validation.minSelections) {
    return {
      valid: false,
      error: t('errors.minSelect', language, { min: validation.minSelections }),
    };
  }

  if (validation.maxSelections && value.length > validation.maxSelections) {
    return {
      valid: false,
      error: t('errors.maxSelect', language, { max: validation.maxSelections }),
    };
  }

  return { valid: true };
}

function validateNumber(
  value: number,
  validation: QuestionValidation | undefined,
  language: Language
): ValidationResult {
  if (!validation) return { valid: true };

  if (validation.min !== undefined && value < validation.min) {
    return { valid: false, error: `Minimum value is ${validation.min}` };
  }

  if (validation.max !== undefined && value > validation.max) {
    return { valid: false, error: `Maximum value is ${validation.max}` };
  }

  return { valid: true };
}
